import React from 'react';
import { useField } from 'formik';
import { Box, FormHelperText, Grow, Tooltip, TooltipProps } from '@mui/material';
import { makeStyles  } from 'tss-react/mui';
import WarningIcon from '@mui/icons-material/Warning';
import { CalendarDateService, generate24HoursDayTimeRange, generateDayTimeRange } from '@uplift/services';
import { TimeRange } from '@uplift/types';
import { useL10n } from '@uplift/hooks';
import { Warning } from '@mui/icons-material';
import { TIME_RANGE_DATE_VALUE_FORMAT } from './constants';
import { TimePickerRaw } from './TimePicker.component';


const useStyles = makeStyles()(theme => ({
  warning: {
    color: theme.palette.warning.main,
  },
}));


export type TimeRangeInputProps = {
  name: string;
  color?: 'primary' | 'secondary';
  inlineWarning?: boolean;
  startTitle?: string;
  endTitle?: string;
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  warningOnLessThenHour?: boolean;
  lessThenHourWarningTitle: string;
  searchSelect?: boolean;
  endTimeLaterThanStartTime?: boolean;
  endTimeLaterThanStartTimeAsTooltip?: boolean;
  endTimeLaterThanStartTimeMessage?: string;
  minuteIncrement?: number;
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  disableInclusiveEnd?: boolean;
};

const useWarningMessageStyles = makeStyles()(theme => ({
  root: {
    color: theme.palette.warning.darkText,
  },
  icon: {
    color: theme.palette.warning.main,
  }
}));

export const WarningMessage: React.FC = ({ children }) => {
  const { classes } = useWarningMessageStyles();

  return (
    <Box display="flex" alignItems="center" mt={1} className={classes.root}>
      <Box display="flex" mr={1} className={classes.icon}><WarningIcon fontSize="small" /></Box> {children}
    </Box>
  );
};

const useStyledTooltipStyles = makeStyles<{ topSpacing: number }>({ name: 'StyledTooltip' })((theme, { topSpacing }) => ({
  tooltipPlacementRight: {
    top: theme.spacing(topSpacing / 2),
  },
  popper: {
    zIndex: 1
  }
}));

const StyledTooltip: React.FC<TooltipProps & { topSpacing: number }> = ({ topSpacing, ...props }) => {
  const { classes } = useStyledTooltipStyles({ topSpacing });

  return <Tooltip {...props} classes={classes} />;
};


export type TimeRangeInputValue = { id: number } & TimeRange;
export const TimeRangeInput: React.FC<TimeRangeInputProps> = ({ endTimeLaterThanStartTime = true, ...props }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const endTimeLaterThanStartTimeMessage = props.endTimeLaterThanStartTimeMessage ?? l10n.validations.chooseEndTimeLaterThanStartTime;

  const [ field, meta, helpers ] = useField<TimeRangeInputValue>({
    name: props.name,
    validate: (value?: TimeRangeInputValue) => {
      if (!value) return;

      if (!value.end) {
        return l10n.validations.chooseEndTime;
      }

      const start = CalendarDateService.date(value.start, TIME_RANGE_DATE_VALUE_FORMAT);
      const end = CalendarDateService.date(value.end, TIME_RANGE_DATE_VALUE_FORMAT).add(fieldValue.end === '00:00:00' ? 1 : 0, 'day');

      if (endTimeLaterThanStartTime && start.isSameOrAfter(end) && value.end !== '00:00:00') {
        return endTimeLaterThanStartTimeMessage;
      }

      return;
    },
  });

  const fieldValue = React.useMemo(() => field.value || { id: 0, start: '00:00:00', end: '01:00:00' }, [ field.value ]);

  const error = React.useMemo(() => {
    if (meta.error) {
      const asTooltip = (props.endTimeLaterThanStartTimeAsTooltip && meta.error === endTimeLaterThanStartTimeMessage) ? 'end' : undefined;

      return { error: !!meta.error, children: meta.error, asTooltip: asTooltip, message: meta.error };
    }

    const start = CalendarDateService.date(fieldValue.start, TIME_RANGE_DATE_VALUE_FORMAT);
    const end = CalendarDateService.date(fieldValue.end, TIME_RANGE_DATE_VALUE_FORMAT).add(fieldValue.end === '00:00:00' ? 1 : 0, 'day');

    if (props.warningOnLessThenHour && end.diff(start, 'minutes') < 60) {
      return {
        className: classes.warning,
        children: (
          <WarningMessage>
            {props.lessThenHourWarningTitle}
          </WarningMessage>
        ),
        message: props.lessThenHourWarningTitle
      };
    }

    return;
  }, [ classes.warning, endTimeLaterThanStartTimeMessage, fieldValue.end, fieldValue.start, meta.error, props.endTimeLaterThanStartTimeAsTooltip, props.lessThenHourWarningTitle, props.warningOnLessThenHour ]);

  const start = React.useMemo(() => {
    const parsed = CalendarDateService.date(fieldValue.start, TIME_RANGE_DATE_VALUE_FORMAT);

    return CalendarDateService.date(0).startOf('day').add(parsed.hour() * 60 + parsed.minute(), 'minutes');
  }, [ fieldValue ]);

  const hasError = meta.touched && error?.error;
  const hasWarning = error && !error?.error;
  const isTextWarning = !props.inlineWarning && hasWarning;
  const pt = props.startTitle || props.endTitle ? 3 : 0;
  const errorMessageSpacing = props.inlineWarning ? 4 : 0;

  const titles = {
    start: props.startTitle,
    end: props.endTitle
  };

  const renderTimeSelect = (placement: 'start' | 'end') => {
    const slotsGenerator = endTimeLaterThanStartTime && placement === 'end' ? generateDayTimeRange : generate24HoursDayTimeRange;

    return (
      <TimePickerRaw
        color={props.color}
        label={titles[placement] ?? ''}
        name={placement}
        value={fieldValue[placement]}
        setValue={(v: string) => helpers.setValue({ ...fieldValue, [placement]: v })}
        onBlur={() => helpers.setTouched(true)}
        error={hasError}
        slotsGenerator={slotsGenerator}
        showAfter={placement === 'end' ? start : undefined}
        inclusive={placement === 'end' ? !props.disableInclusiveEnd : false}
        minuteIncrement={props.minuteIncrement}
        searchSelect={props.searchSelect}
      />
    );
  };

  return (
    <Box>
      <StyledTooltip
        title={error?.message ?? ''}
        placement="right"
        arrow
        open={!!error?.asTooltip}
        topSpacing={pt}
        TransitionProps={{ timeout: { exit: 0, enter: 300 } }}
      >
        <Box display="flex" alignItems="center" width={props.fullWidth ? '100%' : 'fit-content'}>
          <Box pt={pt}>
            {props.prepend}
            {props.inlineWarning && (
              <Box width="24px" mr={1} color="warning.main" display="flex" alignItems="center" justifyContent="center">
                <Grow in={hasWarning}>
                  <Warning />
                </Grow>
              </Box>
            )}
          </Box>
          <Box flex={1} minWidth="116px">
            {renderTimeSelect('start')}
          </Box>
          <Box width="30px" pt={pt} display="flex" alignItems="center" justifyContent="center">
        –
          </Box>
          <Box flex={1} minWidth="116px">
            {renderTimeSelect('end')}
          </Box>
          <Box pt={pt}>
            {props.append}
          </Box>
        </Box>
      </StyledTooltip>
      {(hasError || isTextWarning) && !error.asTooltip && (
        <Box pl={errorMessageSpacing}>
          <FormHelperText {...error} />
        </Box>
      )}
      {(!(hasError || isTextWarning) || error.asTooltip) && props.helperText && (
        <FormHelperText>
          {props.helperText}
        </FormHelperText>
      )}
    </Box>
  );
};
